import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Sparkles,
  Save,
  ShieldCheck,
  KeyRound,
  Crown,
  ArrowRight,
  Star,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Join J.T.L — the single entry page replacing "Memberships" in the nav.
 *
 * Layout:
 *   Left  → Free enrollment card with a quick signup form.
 *   Right → Paid-subscription gateway ("Join J.T.L") — routes to the
 *           hidden /subscriptions page with all tiers.
 */
export default function JoinPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const submitFree = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/register", {
        email: form.email,
        password: form.password,
        name: form.name,
        role: "client",
      });
      try {
        window.localStorage.setItem("sasha_token", r.data.token);
      } catch (_) {
        /* ignore */
      }
      toast.success(
        r.data?.user?.membership_number
          ? `Welcome to Janté. Your member number: ${r.data.user.membership_number}`
          : "Welcome to Janté."
      );
      await refresh();
      navigate("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="join-page">
      {/* Hero */}
      <div className="max-w-3xl mx-auto text-center fade-up">
        <div className="overline text-[var(--brand-red)]">Janté Luxury Travel</div>
        <h1 className="font-display text-5xl sm:text-6xl text-[var(--emerald)] mt-4 leading-[1.05]">
          Join{" "}
          <span
            className="text-[var(--gold)]"
            style={{
              fontFamily:
                "Outfit, Inter, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
              fontStyle: "normal",
              fontWeight: 500,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fontSize: "0.6em",
              verticalAlign: "0.18em",
            }}
            data-testid="join-jtl-wordmark"
          >
            JTL
          </span>
        </h1>
        <p
          className="mt-5 text-[var(--ink-2)]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.3rem" }}
          data-testid="join-hero-subhead"
        >
          Sasha is fun and always free — get Sasha and centralized bookings by joining JTL.
        </p>
      </div>

      {/* Side-by-side — Free enrollment ⟷ Paid subscription gateway */}
      <div className="grid lg:grid-cols-2 gap-8 mt-14" data-testid="join-split">
        {/* LEFT — Free enrollment */}
        <div className="lux-card flex flex-col" data-testid="join-free-card">
          <div className="flex items-center gap-2">
            <Sparkles size={18} strokeWidth={1.25} className="text-[var(--gold)]" />
            <div className="overline">Free enrollment</div>
          </div>
          <h2 className="font-display text-4xl text-[var(--emerald)] mt-3 leading-tight">
            Sasha is always free&hellip;
          </h2>
          <p className="text-sm text-[var(--ink-2)] mt-3">
            You'll recall travel plans for 30 days with your reference number — your Janté Travel
            Concierge's portal. Enroll, tell Sasha how you travel, and upgrade to book with a
            dedicated live concierge or anytime on your JTL app.
          </p>

          <ul className="mt-6 space-y-3 text-sm text-[var(--ink-2)]">
            <li className="flex items-start gap-3">
              <Save size={15} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
              <div>
                <div className="text-[var(--emerald)] font-display text-lg leading-tight">
                  Unlimited trip storage
                </div>
                Every itinerary Sasha builds, saved under your reference.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <KeyRound size={15} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
              <div>
                <div className="text-[var(--emerald)] font-display text-lg leading-tight">
                  Sasha remembers
                </div>
                Preferences, past trips, dietary notes — carried across every visit.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ShieldCheck size={15} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
              <div>
                <div className="text-[var(--emerald)] font-display text-lg leading-tight">
                  No commitment
                </div>
                Free forever. Upgrade when you want live concierge care.
              </div>
            </li>
          </ul>

          {user ? (
            <div className="mt-auto pt-8">
              <div className="lux-card text-center !py-6 !bg-[var(--ivory-2)]" data-testid="join-free-already">
                <CheckCircle2 size={22} strokeWidth={1.25} className="text-[var(--emerald)] mx-auto" />
                <div className="mt-2 font-display text-xl text-[var(--emerald)]">
                  You're already enrolled.
                </div>
                {user.membership_number && (
                  <div className="mt-1 text-sm text-[var(--gold)] tracking-[0.2em]">
                    Client No. {user.membership_number}
                  </div>
                )}
                <Link to="/portal" className="btn-primary mt-5" data-testid="join-free-portal-btn">
                  Open your portal
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submitFree} className="mt-8 space-y-4" data-testid="join-free-form">
              <div>
                <div className="overline mb-1">Name</div>
                <input
                  className="lux-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="join-free-name"
                />
              </div>
              <div>
                <div className="overline mb-1">Email</div>
                <input
                  type="email"
                  required
                  className="lux-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="join-free-email"
                />
              </div>
              <div>
                <div className="overline mb-1">Password</div>
                <input
                  type="password"
                  required
                  minLength={6}
                  className="lux-input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  data-testid="join-free-password"
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full mt-3"
                data-testid="join-free-submit"
              >
                {busy ? "Creating…" : "Create my free account"}
                <ArrowRight size={14} strokeWidth={1.5} />
              </button>
              <p className="text-[11px] text-[var(--muted)] text-center">
                Two minutes. No card required.
              </p>
              <p className="text-xs text-[var(--ink-2)] text-center">
                Already a guest?{" "}
                <Link to="/login" className="text-[var(--gold)] hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>

        {/* RIGHT — Paid-subscription gateway "Join J.T.L" */}
        <div
          className="lux-card-dark flex flex-col relative overflow-hidden"
          data-testid="join-paid-card"
        >
          <div className="flex items-center gap-2">
            <Crown size={20} strokeWidth={1.25} className="text-[var(--gold)]" />
            <div className="overline text-[var(--gold)]">Janté subscriptions</div>
          </div>
          <h2 className="font-display text-4xl mt-3 leading-tight text-[var(--ivory)]">
            Join <em className="not-italic text-[var(--gold)]">J.T.L</em>
          </h2>
          <p className="text-sm opacity-90 mt-3">
            Step inside a world where Sasha is the opening act. Your assigned concierge, the
            Janté host network, and member-only care — all in one quietly powerful subscription.
          </p>

          <ul className="mt-8 space-y-4 text-sm" data-testid="join-paid-highlights">
            <li className="flex items-start gap-3">
              <Star size={16} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-lg leading-tight text-[var(--gold)]">Access</div>
                <span className="opacity-85">from $24.50/mo · 1 itinerary/month · queued concierge</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Star size={16} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-lg leading-tight text-[var(--gold)]">Joiner</div>
                <span className="opacity-85">from $75/mo · your own concierge · priority Sasha</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Star size={16} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-lg leading-tight text-[var(--gold)]">Royal Crown</div>
                <span className="opacity-85">$399/mo · unlimited itineraries · premium stays</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Crown size={16} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-lg leading-tight text-[var(--gold)]">Platinum</div>
                <span className="opacity-85">$12,500 prepaid · 7 vacations for two · signature</span>
              </div>
            </li>
          </ul>

          <div className="mt-auto pt-10 flex flex-col gap-3">
            <Link
              to="/subscriptions"
              className="btn-gold w-full justify-center"
              data-testid="join-paid-cta"
            >
              Join J.T.L <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link
              to="/tenant-thrive"
              className="text-[10px] uppercase tracking-[0.22em] text-[var(--ivory)]/70 hover:text-[var(--gold)] text-center"
              data-testid="join-paid-mission"
            >
              Learn about Janté Tenant Thrive →
            </Link>
          </div>
        </div>
      </div>

      {/* Host pathway footer */}
      <div className="mt-16 lux-card text-center" data-testid="join-host-pathway">
        <div className="overline">Own a property?</div>
        <h3 className="font-display text-3xl text-[var(--emerald)] mt-3">
          Explore hosting with Janté
        </h3>
        <p className="text-sm text-[var(--ink-2)] mt-3 max-w-xl mx-auto">
          Bronze · Silver · Gold tiers — or hand the whole operation to JTL Property Assistance.
          Apply privately; you'll hear back within a week.
        </p>
        <div className="mt-5 flex flex-wrap gap-3 justify-center">
          <Link to="/hosts" className="btn-primary" data-testid="join-host-btn">
            Discover hosting
          </Link>
          <Link to="/host-signin" className="btn-ghost" data-testid="join-host-signin">
            Host sign-in
          </Link>
        </div>
      </div>
    </div>
  );
}
